import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Thermometer,
  Droplets,
  Battery,
  MapPin,
  Calendar,
  Settings,
  TrendingUp,
  AlertTriangle,
  Wifi,
  WifiOff
} from 'lucide-react';
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { createClient } from '@supabase/supabase-js';


const supabaseUrl = "https://iudzzeqvihyxbzdvooeq.supabase.co";
const supabaseAnonKey =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Iml1ZHp6ZXF2aWh5eGJ6ZHZvb2VxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTY5Nzc4MDcsImV4cCI6MjA3MjU1MzgwN30.KcSOr8Oa0fnSaeWWBNXZy0osMOdL2od_tJ3vOV_-c44";

const supabase = createClient(supabaseUrl, supabaseAnonKey);

const SensorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [sensor, setSensor] = useState(null);
  const [historicalData, setHistoricalData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSensorData = async () => {
      setLoading(true);
      // Récupérer le capteur avec les seuils et info devices
      const { data: sensorData, error: sensorError } = await supabase
        .from('sensors')
        .select(`*, devices(name, location, battery, status), alert_thresholds`)
        .eq('id', id)
        .single();

      if (sensorError || !sensorData) {
        setSensor(null);
        setLoading(false);
        return;
      }

      setSensor(sensorData);

      // Récupérer les données historiques de ce capteur (dernier 24h max)
      const { data: history, error: historyError } = await supabase
        .from('sensor_data')
        .select('measured_at, value, value_type')
        .eq('sensor_id', id)
        .order('measured_at', { ascending: true })
        .limit(100);

      if (historyError) {
        setHistoricalData([]);
      } else {
        // Traiter et formater les données pour le graphe
        const filteredData = [];

        for (let i = 0; i < history.length; i++) {
          const entry = history[i];
          let existing = filteredData.find(d => d.time === new Date(entry.measured_at).getHours() + 'h');

          if (!existing) {
            existing = { time: new Date(entry.measured_at).getHours() + 'h' };
            filteredData.push(existing);
          }
          if (entry.value_type === 'humidity') existing.humidity = entry.value;
          else if (entry.value_type === 'temperature') existing.temperature = entry.value;
          else existing[entry.value_type] = entry.value;
        }
        setHistoricalData(filteredData);
      }
      setLoading(false);
    };

    fetchSensorData();
  }, [id]);

  if (loading) {
    return <div className="p-8 text-center text-lg text-gray-500">Chargement des données...</div>;
  }

  if (!sensor) {
    return (
      <div className="p-8 max-w-xl mx-auto text-center text-gray-500">
        <AlertTriangle className="mx-auto mb-4 h-16 w-16" />
        Capteur non trouvé.
        <button
          onClick={() => navigate('/')}
          className="mt-6 px-6 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
        >
          Retour
        </button>
      </div>
    );
  }

  const { devices: device } = sensor;
  const alertThresholds = sensor.alert_thresholds || {};
  const StatusIcon = () => {
    switch(sensor.status) {
      case 'active': return <Wifi className="text-green-600" />;
      case 'warning': return <AlertTriangle className="text-yellow-600" />;
      case 'offline': return <WifiOff className="text-red-600" />;
      default: return <WifiOff className="text-gray-400" />;
    }
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleString('fr-FR');

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate('/')} className="flex items-center space-x-2 text-indigo-600 hover:text-indigo-800">
          <ArrowLeft /> <span>Retour</span>
        </button>

        <div>
          <h1 className="text-3xl font-bold mb-1">{sensor.name}</h1>
          <div className="flex items-center space-x-4 text-gray-600 text-sm">
            <div className="flex items-center"><MapPin className="mr-1" /> {device?.location || sensor.location}</div>
            <div className="flex items-center"><StatusIcon /> {sensor.status}</div>
          </div>
        </div>

        <button className="flex items-center space-x-2 border border-gray-300 rounded px-3 py-1 hover:bg-gray-100">
          <Settings /> <span>Configurer</span>
        </button>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Humidity */}
        <div className="glass-effect p-5 rounded-xl bg-white shadow">
          <div className="flex items-center justify-between mb-4">
            <Droplets className="text-blue-600" size={24} />
            {(sensor.humidity < alertThresholds.humidity_min || sensor.humidity > alertThresholds.humidity_max) && (
              <AlertTriangle className="text-red-600" size={20} />
            )}
          </div>
          <p className="text-5xl font-bold">{sensor.humidity?.toFixed(1) || '-' }%</p>
          <p className="text-gray-600">Humidité</p>
          <p className="text-sm text-gray-400">Seuil optimal: {alertThresholds.humidity_min}-{alertThresholds.humidity_max}%</p>
        </div>

        {/* Temperature */}
        <div className="glass-effect p-5 rounded-xl bg-white shadow">
          <div className="flex items-center justify-between mb-4">
            <Thermometer className="text-orange-500" size={24} />
            {(sensor.temperature < alertThresholds.temperature_min || sensor.temperature > alertThresholds.temperature_max) && (
              <AlertTriangle className="text-red-600" size={20} />
            )}
          </div>
          <p className="text-5xl font-bold">{sensor.temperature?.toFixed(1) || '-'}°C</p>
          <p className="text-gray-600">Température</p>
          <p className="text-sm text-gray-400">Seuil optimal: {alertThresholds.temperature_min}-{alertThresholds.temperature_max}°C</p>
        </div>

        {/* Battery */}
        <div className="glass-effect p-5 rounded-xl bg-white shadow">
          <div className="flex items-center justify-between mb-4">
            <Battery className="text-green-600" size={24} />
            <p className="text-sm text-gray-500">Dernière mise à jour</p>
          </div>
          <p className="text-5xl font-bold">{sensor.battery ?? '-'}%</p>
          <p className="text-gray-600">
            {sensor.battery > 50 ? 'Niveau optimal' : sensor.battery > 20 ? 'Batterie faible' : 'Batterie critique'}
          </p>
          <div className="w-full h-2 bg-gray-200 rounded mt-2">
            <div style={{ width: `${sensor.battery || 0}%` }} className={`h-2 rounded ${
              sensor.battery > 50 ? 'bg-green-600' : sensor.battery > 20 ? 'bg-yellow-500' : 'bg-red-600'
            }`} />
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        {/* Humidity Chart */}
        <div className="glass-effect p-5 rounded-xl bg-white shadow">
          <h2 className="text-lg font-semibold mb-4 flex items-center space-x-2">
            <Droplets className="text-blue-500" /> Humidité (24h)
          </h2>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={historicalData}>
              <defs>
                <linearGradient id="humidityGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#e0e0e0" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Area type="monotone" dataKey="humidity" stroke="#3B82F6" fill="url(#humidityGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Temperature Chart */}
        <div className="glass-effect p-5 rounded-xl bg-white shadow">
          <h2 className="text-lg font-semibold mb-4 flex items-center space-x-2">
            <Thermometer className="text-orange-500" /> Température (24h)
          </h2>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={historicalData}>
              <CartesianGrid stroke="#e0e0e0" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="temperature" stroke="#fb923c" strokeWidth={3} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default SensorDetail;
